import { isRecord } from '@revelio/core/is-record';

import type {
  Position,
  PositionedItem,
} from '../../PageConfigProvider/types/item';

const CHART_CLIPBOARD_KIND = 'revelio-custom-dash-chart';
const CHART_CLIPBOARD_VERSION = 1;

export type ChartClipboardConfig = Omit<PositionedItem, 'id' | 'slotId'>;

type ChartClipboardPayload = {
  kind: typeof CHART_CLIPBOARD_KIND;
  version: typeof CHART_CLIPBOARD_VERSION;
  chart: ChartClipboardConfig;
};

const isGridInteger = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value);

const isPosition = (value: unknown): value is Position => {
  if (!isRecord(value)) return false;

  return (
    isGridInteger(value.x) &&
    isGridInteger(value.y) &&
    isGridInteger(value.width) &&
    isGridInteger(value.height) &&
    value.x >= 0 &&
    value.y >= 0 &&
    value.width > 0 &&
    value.height > 0
  );
};

const isChartClipboardConfig = (
  value: unknown
): value is ChartClipboardConfig => {
  if (!isRecord(value) || !isPosition(value.position)) return false;
  if (typeof value.title !== 'string' && value.title !== null) return false;
  if (value.plot !== undefined && !isRecord(value.plot)) return false;
  if (value.info !== undefined && !isRecord(value.info)) return false;

  return true;
};

export const serializeChartClipboardConfig = (item: PositionedItem): string => {
  const { id: _id, slotId: _slotId, ...chart } = item;
  const payload: ChartClipboardPayload = {
    kind: CHART_CLIPBOARD_KIND,
    version: CHART_CLIPBOARD_VERSION,
    chart,
  };

  return JSON.stringify(payload, null, 2);
};

export const parseChartClipboardConfig = (
  value: string
): ChartClipboardConfig | null => {
  try {
    const parsed: unknown = JSON.parse(value);
    if (!isRecord(parsed)) return null;
    if (parsed.kind !== CHART_CLIPBOARD_KIND) return null;
    if (parsed.version !== CHART_CLIPBOARD_VERSION) return null;
    if (!isChartClipboardConfig(parsed.chart)) return null;

    return parsed.chart;
  } catch {
    return null;
  }
};
